// src/app/core/services/custom-image-upload.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Observer, throwError, timer } from 'rxjs';
import { CREATE_IMSGE_UPLOAD_URLS } from './custom-image-uploader.service.graphql';
import { CreateCustomizableImageUploadUrlsMutation, CreateCustomizableImageUploadUrlsMutationVariables } from '../../../common/generated-types';
import { catchError, map, mergeMap, retry, retryWhen, timeout } from 'rxjs/operators';
import { DataService } from '../data/data.service';
import { UPLOAD_IMAGE_TIMEOUT_MS, UPLOAD_INITIAL_DELAY_MS, UPLOAD_MAX_RETRIES } from '../../../common/constants';
import { CreateCustomizableImageUploadUrlsResult } from '../../../common/interfaces';

@Injectable({ providedIn: 'root' })
export class CustomImageUploadService {
  constructor(
    private dataService: DataService,
    private http: HttpClient,
  ) {}

  getSignedUploadUrls(
    filename: string,
    productVariantId: string,
    retryCount = UPLOAD_MAX_RETRIES,
    retryDelayMs = UPLOAD_INITIAL_DELAY_MS
  ) {
    return this.dataService
      .mutate<
        CreateCustomizableImageUploadUrlsMutation,
        CreateCustomizableImageUploadUrlsMutationVariables
      >(CREATE_IMSGE_UPLOAD_URLS, {
          input: { filename, productVariantId }
      }).pipe(
        map(res => {
          const payload = res?.createCustomizableImageUploadUrls;
          if (!payload) {
            // Covers 200 + GraphQL-error case
            throw new Error('Server returned no upload URLs');
          }
          return payload;
        }),
        retryWhen(errors =>
          errors.pipe(
            mergeMap((err, i) =>
              i < retryCount
                ? timer(retryDelayMs).pipe(mergeMap(() => [err]))
                : throwError(() => err)
            )
          )
        ),
        timeout(UPLOAD_IMAGE_TIMEOUT_MS),
      );
  }

  uploadToS3(
    url: string,
    file: Blob,
    retryCount = UPLOAD_MAX_RETRIES,
    retryDelayMs = UPLOAD_INITIAL_DELAY_MS,
  ): Observable<void> {
    return this.http.put<void>(
      url,
      file,
      {
        headers: new HttpHeaders({ 'Content-Type': file.type }),
        reportProgress: false,
        responseType: 'text' as any,
      }
    ).pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((err, i) =>
            i < retryCount
              ? timer(retryDelayMs).pipe(mergeMap(() => [err]))
              : throwError(() => err)
          )
        )
      ),
      timeout(UPLOAD_IMAGE_TIMEOUT_MS),
    );
  }
}
